import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const MERCHANT_ROLES = ['farmer', 'farmer_staff'];

const MerchantLoginPage = () => {
  const [searchParams] = useSearchParams();
  const selectedRole = searchParams.get('role') || 'farmer';
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const roleLabel = selectedRole === 'farmer_staff' ? 'Merchant Staff' : 'Merchant';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.user && MERCHANT_ROLES.includes(result.user.role)) {
        navigate('/merchant/dashboard');
      } else {
        setError('This account does not have merchant portal access.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 50%, #F7FEE7 100%)' }}>
      {/* Left Branding Panel */}
      <div className="auth-sidebar d-none d-lg-flex" style={{
        flex: '0 0 420px', background: 'linear-gradient(180deg, #059669 0%, #047857 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '60px 40px', color: '#FFF', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)', pointerEvents: 'none' }} />
        <div style={{ fontSize: '80px', marginBottom: '24px' }}>🏪</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, textAlign: 'center', marginBottom: '16px' }}>
          {t('sellerCentral')}
        </h2>
        <p style={{ textAlign: 'center', opacity: 0.8, fontSize: '15px', lineHeight: 1.7, maxWidth: '300px' }}>
          {t("sellerCentralDesc")} 
        </p>
        <div style={{ marginTop: '40px', display: 'flex', gap: '24px', opacity: 0.7 }}>
          {[`📦 ${t('products')}`, `📊 ${t('analytics')}`, `🎫 ${t('coupons')}`].map(item => (
            <span key={item} style={{ fontSize: '12px', fontWeight: 600 }}>{item}</span>
          ))}
        </div>
      </div>

      {/* Right Form */}
      <div className="auth-form-container" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'block', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: '#B91C1C', fontFamily: "'Poppins', sans-serif" }}>
              eBuy<span style={{ color: '#EAB308' }}>Wiz</span>
            </span>
          </Link>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', color: '#1F2937', marginBottom: '4px' }}>
            {t('merchantSignIn')}
          </h1>
          <p style={{ color: '#059669', fontWeight: 600, fontSize: '15px', marginBottom: '28px' }}>{roleLabel}</p>

          {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t("emailAddress")}*</label>
              <input type="email" required style={inputStyle} placeholder="seller@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t("password")}*</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} required style={{ ...inputStyle, paddingRight: '44px' }} placeholder={t("password")} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF', fontSize: '18px' }} title={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <Link to="/forgot-password" style={{ color: '#059669', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>{t("forgotPassword")}</Link>
            </div>
            <button type="submit" disabled={loading} style={{ ...btnStyle, background: '#059669' }}>
              {loading ? '...' : t('signIn')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#6B7280' }}>
            {t("dontHaveStore")}{' '}
            <Link to="/register/merchant" style={{ color: '#059669', fontWeight: 600, textDecoration: 'none' }}>{t("createOne")} →</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#9CA3AF' }}>
            <Link to="/choose-role" style={{ color: '#9CA3AF', textDecoration: 'none' }}>{t("backToAllRoles")}</Link>
          </p>
        </div>
      </div>

      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Playfair+Display:ital,wght@0,700;1,700&display=swap" rel="stylesheet" />
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '14px 16px', border: '2px solid #E5E7EB', borderRadius: '10px',
  fontSize: '15px', transition: 'border-color 0.2s', outline: 'none', boxSizing: 'border-box',
  fontFamily: "'Poppins', sans-serif",
};

const btnStyle = {
  width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
  color: '#FFF', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
  fontFamily: "'Poppins', sans-serif", letterSpacing: '0.5px',
};

export default MerchantLoginPage;
